import PropTypes from "prop-types";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { get } from "lodash";

import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { REQUIRED_MARK } from "./fieldClasses";

// base-ui Select items need a value, and an empty string can't be told apart
// from "no choice yet", so a sentinel maps back to null when cleared. Emitting
// null (not "") matches the nullable single-select Zod schema.
const CLEAR = "__clear__";

const FormSelect = ({
  name,
  label,
  required,
  options,
  placeholder = "Select…",
  clearable,
  disabled,
  id,
  onValueChange,
  renderOption,
  "aria-label": ariaLabel,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;
  // base-ui SelectValue renders the raw value by default; map it back to the
  // option's label so the trigger shows the label, not the stored value.
  const optionByValue = useMemo(
    () => new Map(options.map(o => [o.value, o])),
    [options]
  );
  const optionLabel = o => (renderOption ? renderOption(o) : o.text);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const hasValue = field.value != null && field.value !== "";
        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <Select
              value={field.value ?? null}
              onValueChange={next => {
                const resolved = next === CLEAR ? null : next;
                field.onChange(resolved);
                onValueChange?.(resolved);
              }}
              disabled={disabled}
            >
              <SelectTrigger
                id={inputId}
                aria-label={ariaLabel}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                className="tw:w-full"
              >
                <SelectValue placeholder={placeholder}>
                  {value => {
                    const option = optionByValue.get(value);
                    return (option && optionLabel(option)) ?? value;
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {clearable && hasValue && (
                  <SelectItem value={CLEAR}>Clear</SelectItem>
                )}
                {options.map(o => (
                  <SelectItem key={o.key ?? o.value} value={o.value}>
                    {optionLabel(o)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  clearable: PropTypes.bool,
  search: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  onValueChange: PropTypes.func,
  renderOption: PropTypes.func,
  "aria-label": PropTypes.string,
};

export default FormSelect;
